import CloseCircle from 'assets/icons/CloseCircle';
import OverlaySpinner from 'components/OverlaySpinner';
import Drawer from 'components/ui/Drawer';
import { BaseButtonSm } from 'components/ui/BaseButton';
import COLOR from 'constants/color';
import { PAGES } from 'constants/routes';
import { PackAndFocusEntityType } from 'enums';
import { PLATFORMS } from 'constants/general';
import HabitPackPdf from 'dashboard/marketplace/habitPackPdf';
import InquiryNoticeModal from 'dashboard/inquiry/InquiryNoticeModal';
import LoadingIndicator from 'components/common/LoadingIndicator';
import { t } from 'services/i18n.service';
import { useAppSelectorShallow } from 'store/hooks';
import { packPreviewDrawerSelector } from 'store/reducer/habit-pack/selectors';
import { usePackPreviewDrawer } from '../../hooks/usePackPreviewDrawer';
import { DetailsPageWrapperContext } from 'dashboard/wrappers/DetailsPageWrapper';
import { PackPreviewContext } from './PackPreviewContext';
import PackPreviewTitle from './PackPreviewTitle';
import HabitPackPreviewBody from './HabitPackPreviewBody';
import FocusTemplatePreviewBody from './FocusTemplatePreviewBody';
import PackPreviewHabitPackActions from './PackPreviewHabitPackActions';
import PackPreviewFocusTemplateActions from './PackPreviewFocusTemplateActions';
import { hasMarketplaceAuthSession } from 'utils/marketplace.util';

const PackPreviewDrawer = () => {
  const {
    isPackPreviewOpen,
    previewType,
    previewVariant,
    isPreviewLoading,
    closePreview
  } = usePackPreviewDrawer();

  const {
    currentViewedPack,
    isHabitPackPdfShowed,
    isUserUpsertingPack,
    platform,
    isInquiryModalShown,
    isUserInstalledPacksFetching,
    isUserInstalledFocusTemplatesFetching,
    currentViewedFocusTemplate
  } = useAppSelectorShallow(packPreviewDrawerSelector);

  if (!isPackPreviewOpen || !previewType || !previewVariant) {
    return null;
  }

  const page =
    previewType === PackAndFocusEntityType.HABIT_PACK
      ? PAGES.HABIT_PACK
      : PAGES.FOCUS_TEMPLATE;

  const shouldShowActions = hasMarketplaceAuthSession();

  const isContentLoading =
    previewType === PackAndFocusEntityType.HABIT_PACK
      ? isPreviewLoading || !currentViewedPack
      : isPreviewLoading || !currentViewedFocusTemplate;

  const isActionsLoading =
    previewType === PackAndFocusEntityType.HABIT_PACK
      ? isUserInstalledPacksFetching
      : isUserInstalledFocusTemplatesFetching;

  const renderPreviewActions = () => {
    if (isActionsLoading) {
      return (
        <LoadingIndicator
          dims='~w-4/8 ~h-4/8'
          additionalStyles='~my-4/6 mx-auto'
        />
      );
    }
    if (previewType === PackAndFocusEntityType.HABIT_PACK) {
      return <PackPreviewHabitPackActions variant={previewVariant} />;
    }
    return <PackPreviewFocusTemplateActions variant={previewVariant} />;
  };

  const renderPreviewDetails = () => {
    if (previewType === PackAndFocusEntityType.HABIT_PACK) {
      return <HabitPackPreviewBody />;
    }
    return <FocusTemplatePreviewBody />;
  };

  return (
    <PackPreviewContext.Provider
      value={{
        isPreview: true,
        variant: previewVariant,
        onClose: closePreview
      }}>
      <DetailsPageWrapperContext.Provider value={{ page }}>
        <Drawer
          isOpen={isPackPreviewOpen}
          onClose={closePreview}
          side='right'
          width='60vw'
          className='flex flex-col max-h-[100vh]'
          header={t('market.view_more_details')}
          closeOnOverlayClick
          closeButton={
            <BaseButtonSm
              icon={
                <CloseCircle
                  styles='~w-4/5 h-auto'
                  fill={COLOR.PRIMARY.LIGHT_900}
                />
              }
              variant='icon'
              onClick={closePreview}
              aria-label={t('btn_cancel')}
            />
          }>
          {isContentLoading ? (
            <LoadingIndicator
              dims='~w-4/10 ~h-4/10'
              additionalStyles='my-[20vh] mx-auto'
            />
          ) : (
            <div
              className='w-full flex flex-col '
              data-testid='pack-preview-drawer-content'>
              <PackPreviewTitle previewType={previewType} />
              {shouldShowActions && (
                <section
                  className='w-full border-b border-gray-200 dark:border-secondary-dark-400 pb-4'
                  aria-label='Preview actions'>
                  {renderPreviewActions()}
                </section>
              )}
              <section
                className='w-full pt-4 flex-1'
                aria-label='Preview details'>
                {renderPreviewDetails()}
              </section>
            </div>
          )}
          {isInquiryModalShown && platform === PLATFORMS.WEB && (
            <InquiryNoticeModal />
          )}
          {isHabitPackPdfShowed &&
            previewType === PackAndFocusEntityType.HABIT_PACK && (
              <HabitPackPdf />
            )}
          {isUserUpsertingPack && <OverlaySpinner title={t('cloning')} />}
        </Drawer>
      </DetailsPageWrapperContext.Provider>
    </PackPreviewContext.Provider>
  );
};

export default PackPreviewDrawer;
